import React, { useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import { useAuth } from '../../../stores/AuthContext';
import './SnowflakeIntegration.css';

interface SnowflakeConnection {
  id?: number;
  name: string;
  account: string;
  username: string;
  password: string;
  warehouse: string;
  database: string;
  schema?: string;
  role?: string;
  description?: string;
}

interface DataSource {
  id: number;
  name: string;
  type: string;
  description: string;
  created_at: string;
}

const SnowflakeIntegration: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState<DataSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [testing, setTesting] = useState(false);
  const [formData, setFormData] = useState<SnowflakeConnection>({
    name: '',
    account: '',
    username: '',
    password: '',
    warehouse: '',
    database: '',
    schema: 'PUBLIC',
    role: '',
    description: '',
  });

  useEffect(() => {
    fetchConnections();
  }, []);

  const fetchConnections = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/ingestion/datasources/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Filter for Snowflake connections only
        const snowflakeConnections = data.filter((ds: DataSource) => ds.type === 'snowflake');
        setConnections(snowflakeConnections);
      }
    } catch (err) {
      console.error('Failed to fetch connections:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/ingestion/snowflake/test/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          account: formData.account,
          username: formData.username,
          password: formData.password,
          warehouse: formData.warehouse,
          database: formData.database,
          schema: formData.schema || 'PUBLIC',
          role: formData.role || '',
        }),
      });

      const data = await response.json();

      if (response.ok && data.status === 'success') {
        setSuccess(true);
      } else {
        setError(data.message || 'Connection test failed');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await fetch(`${API_BASE_URL}/ingestion/datasources/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          name: formData.name,
          type: 'snowflake',
          description: formData.description,
          connection_details: {
            account: formData.account,
            username: formData.username,
            password: formData.password,
            warehouse: formData.warehouse,
            database: formData.database,
            schema: formData.schema || 'PUBLIC',
            role: formData.role || '',
          },
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create connection');
      }

      const data = await response.json();
      setSuccess(true);
      setFormData({
        name: '',
        account: '',
        username: '',
        password: '',
        warehouse: '',
        database: '',
        schema: 'PUBLIC',
        role: '',
        description: '',
      });
      setShowForm(false);
      fetchConnections();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create connection');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/ingestion/datasources/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        fetchConnections();
      } else {
        setError('Failed to delete connection');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete connection');
    }
  };

  return (
    <div className="snowflake-integration">
      <nav className="dashboard-nav">
        <div className="nav-container">
          <div className="logo">
            <span className="logo-icon">🐋</span>
            <span className="logo-text">Apex</span>
          </div>
          <div className="nav-links">
            <Button variant="outlined" onClick={() => navigate('/app/dashboard')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <main className="integration-main">
        <div className="container">
          <div className="integration-header">
            <h1>Snowflake Integration</h1>
            <p className="subtitle">Connect your Snowflake data warehouse to Apex</p>
          </div>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          {success && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Connection created successfully!
            </Alert>
          )}

          <div className="integration-actions">
            <Button variant="contained" onClick={() => setShowForm(!showForm)}>
              {showForm ? 'Cancel' : '+ Add Snowflake Connection'}
            </Button>
          </div>

          {showForm && (
            <div className="connection-form-container">
              <h2>New Snowflake Connection</h2>
              <form onSubmit={handleSubmit} className="connection-form">
                <TextField
                  label="Connection Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  placeholder="e.g., Production Snowflake"
                />

                <div className="form-row">
                  <TextField
                    label="Account"
                    name="account"
                    value={formData.account}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="your-account.snowflakecomputing.com"
                  />
                  <TextField
                    label="Username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="your-username"
                  />
                </div>

                <TextField
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  fullWidth
                  placeholder="Enter your password"
                />

                <div className="form-row">
                  <TextField
                    label="Warehouse"
                    name="warehouse"
                    value={formData.warehouse}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="COMPUTE_WH"
                  />
                  <TextField
                    label="Database"
                    name="database"
                    value={formData.database}
                    onChange={handleInputChange}
                    required
                    fullWidth
                    placeholder="SNOWFLAKE_SAMPLE_DATA"
                  />
                </div>

                <div className="form-row">
                  <TextField
                    label="Schema"
                    name="schema"
                    value={formData.schema}
                    onChange={handleInputChange}
                    fullWidth
                    placeholder="PUBLIC"
                  />
                  <TextField
                    label="Role"
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    fullWidth
                    placeholder="ACCOUNTADMIN"
                  />
                </div>

                <TextField
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Optional description for this connection"
                />

                <div className="form-actions">
                  <Button
                    type="button"
                    variant="outlined"
                    onClick={handleTestConnection}
                    disabled={testing || !formData.account || !formData.username || !formData.password}
                  >
                    {testing ? 'Testing...' : 'Test Connection'}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading || !formData.name || !formData.account}
                  >
                    {loading ? 'Creating...' : 'Create Connection'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          <div className="connections-list">
            <h2>Your Snowflake Connections</h2>
            {connections.length === 0 ? (
              <div className="empty-state">
                <p>No Snowflake connections yet. Create one to get started!</p>
              </div>
            ) : (
              <div className="connections-grid">
                {connections.map((connection) => (
                  <div key={connection.id} className="connection-card">
                    <div className="connection-header">
                      <div className="connection-icon">❄️</div>
                      <h3>{connection.name}</h3>
                    </div>
                    {connection.description && (
                      <p className="connection-description">{connection.description}</p>
                    )}
                    <div className="connection-meta">
                      <span className="connection-type">Snowflake</span>
                      <span className="connection-date">
                        Created {new Date(connection.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="connection-actions">
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate(`/app/integrations/snowflake/${connection.id}`)}
                      >
                        View Details
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDelete(connection.id)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default SnowflakeIntegration;

